import React, { useState, useEffect } from 'react';
import { Col, Row, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import { RiWallet3Line } from 'react-icons/ri';
import { createTopUpOrder, verifyRazorpay } from 'api/customer';
import { PAYMENT_GATEWAY, TOP_UP_INVOICE_STORAGE_KEY, TOP_UP_DONE_MESSAGE } from 'components/common/constant';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import * as _ from 'lodash';

const AddMoneyModal = ({ show, onHide, onSuccess }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [juspayUrl, setJuspayUrl] = useState('');
  const [juspayLoaded, setJuspayLoaded] = useState(false);

  // walletPaymentStatus verifies inside the iframe and reports the result back here
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || _.get(event, 'data.type') !== TOP_UP_DONE_MESSAGE) {
        return;
      }
      if (_.get(event, 'data.paid')) {
        toast.success('Money added successfully');
        onSuccess();
      } else {
        toast.info('Your payment is being processed');
      }
      setJuspayUrl('');
      setJuspayLoaded(false);
      onHide();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  });

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > 500000) {
      toast.error('Amount exceeds maximum allowed payment limit of 5,00,000 INR');
      return;
    }

    setLoading(true);
    try {
      const orderRes = await createTopUpOrder({ amount: parseFloat(amount) });
      const { gateway, razorpay_order_id, razorpay_key, invoice_id, payment_link } = orderRes.data;

      // Juspay hosts the payment page, it renders inside this modal
      if (gateway === PAYMENT_GATEWAY.JUSPAY) {
        // Survives the top window navigation that netbanking and 3DS trigger
        sessionStorage.setItem(TOP_UP_INVOICE_STORAGE_KEY, invoice_id);
        setJuspayUrl(payment_link);
        return;
      }

      const res = await loadRazorpayScript();
      if (!res) {
        toast.error('Razorpay SDK failed to load. Are you online?');
        setLoading(false);
        return;
      }

      const options = {
        key: razorpay_key,
        amount: parseFloat(amount) * 100,
        currency: 'INR',
        name: 'Charge Zone',
        description: 'Wallet Top-up',
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            const verifyRes = await verifyRazorpay(invoice_id, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.status === 200) {
              toast.success('Money added successfully');
              onSuccess();
              onHide();
            }
          } catch (error) {
            if (!_.get(error, 'data.message')) {
              toast.error('Payment verification failed');
            }
          }
        },
        theme: {
          color: '#3399cc',
        },
      };

      const paymentObject = new window.Razorpay(options);
      onHide();
      paymentObject.open();
    } catch (error) {
      if (!_.get(error, 'data.message')) {
        toast.error('Failed to initiate payment');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setAmount('');
    setLoading(false);
    setJuspayUrl('');
    setJuspayLoaded(false);
  };

  // While paying, the modal shows the gateway page and nothing else
  if (juspayUrl) {
    return (
      <Modal show={show} onHide={onHide} onExited={resetState} centered size="lg" className="add-money-modal">
        {!juspayLoaded && <div className="p-5 text-center">Loading payment page...</div>}
        <iframe
          src={juspayUrl}
          title="Payment"
          onLoad={() => setJuspayLoaded(true)}
          style={{ width: '100%', height: '70vh', border: 0, display: juspayLoaded ? 'block' : 'none' }}
        />
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} onExited={resetState} centered className="add-money-modal">
      <div className="logout-modal-main_page">
        <div className="logout-modal--inner">
          <div className="logout-modal---block_title">
            <div className="inner--block_title">
              <div className="title">
                <RiWallet3Line size={30} color="#fff" />
              </div>
              <p className="sub-title">Add Money to Wallet</p>
            </div>
          </div>
          <div className="logout-modal-input-box__block p-4">
            <Row className="mb-4">
              <Col lg={12}>
                <Input
                  label="Enter Amount (INR)"
                  placeholder="0.00"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <Button className="logout-model--btn cancel--btn w-100" onClick={onHide}>
                  {t('button.cancel')}
                </Button>
              </Col>
              <Col lg={6}>
                <Button className="logout-model--btn remove--btn w-100" onClick={handlePayment} loading={loading} disabled={loading || !amount}>
                  Pay Now
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </Modal>
  );
};

AddMoneyModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddMoneyModal;
